import { DueDiligencePlanOffer } from "../Hooks/useApi/Schemas";
import { DueDiligenceTier } from "../Types-DTOs/DueDiligence";

export const DUE_DILIGENCE_PLANS = ["starter", "agency", "professional", "corporate"] as const;

export type DueDiligencePlanKey = (typeof DUE_DILIGENCE_PLANS)[number];

/**
 * What a credit is worth in euros, to tell paying with credits in money.
 * @todo Fixed here for now, though the backend is the source of truth: MIKE-384.
 */
export const CREDIT_EURO_VALUE = 15;

/** The DD tiers in order, as the plans' examples count them. */
export const DUE_DILIGENCE_TIER_LEVELS: Record<DueDiligenceTier, number> = { basic: 1, professional: 2, premium: 3 };

export interface DueDiligencePlanDetails {
  /** A typical month, to compare against paying each report one-off. */
  example: Partial<Record<DueDiligenceTier, number>>;
}

/** What the pricing one-pager tells of each plan beyond its price and credits. */
export const DUE_DILIGENCE_PLAN_DETAILS: Record<DueDiligencePlanKey, DueDiligencePlanDetails> = {
  starter: { example: { basic: 10 } },
  agency: { example: { basic: 10, professional: 1 } },
  professional: { example: { basic: 10, professional: 3, premium: 1 } },
  corporate: { example: { professional: 20, premium: 5 } },
};

/**
 * The tiers a plan's month of credits can pay: those it covers and, when the
 * costs are known, that fit in its monthly credits.
 * @param plan - The plan on offer.
 * @param creditCosts - What each tier costs in credits; without them only the plan's tiers count.
 * @returns The levels, in order.
 */
export const levelsPlanCanPay = (
  plan: Pick<DueDiligencePlanOffer, "allowedTiers" | "monthlyCredits">,
  creditCosts?: Record<DueDiligenceTier, number> | null
): number[] =>
  (Object.keys(DUE_DILIGENCE_TIER_LEVELS) as DueDiligenceTier[])
    .filter((tier) => !plan.allowedTiers || plan.allowedTiers.includes(tier))
    .filter((tier) => !creditCosts || creditCosts[tier] <= plan.monthlyCredits)
    .map((tier) => DUE_DILIGENCE_TIER_LEVELS[tier]);

/**
 * Which plan of the catalog a Stripe lookup key stands for.
 * @param lookupKey - E.g. dd-subscription-agency-monthly.
 * @returns The plan, or undefined for a key the UI doesn't know.
 */
export const planFromLookupKey = (lookupKey: string): DueDiligencePlanKey | undefined =>
  DUE_DILIGENCE_PLANS.find((plan) => lookupKey === `dd-subscription-${plan}-monthly`);

/**
 * The i18n key of a plan's name, falling back to a generic one for plans the UI doesn't know.
 * @param lookupKey - The plan's Stripe lookup key.
 * @returns A key under dueDiligencePlans.
 */
export const planNameKey = (lookupKey: string): string => {
  const plan = planFromLookupKey(lookupKey);
  return plan ? `dueDiligencePlans.names.${plan}` : "dueDiligencePlans.fallbackName";
};

/**
 * Formats an amount of money always grouping thousands (2.250 €, as in the
 * pricing one-pager): Spanish leaves four-digit numbers ungrouped by default.
 * @param amount - In units, not cents.
 * @param currency - ISO code, e.g. eur.
 * @param language - The UI language.
 * @returns The formatted amount, without decimals when it is a whole number.
 */
export const formatMoney = (amount: number, currency: string, language: string): string => {
  // useGrouping "always" is missing from the ES2020 typings this app builds with.
  const options: Record<string, unknown> = {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    useGrouping: "always",
  };
  return new Intl.NumberFormat(language, options as Intl.NumberFormatOptions).format(amount);
};
